import { PlusOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { Table, type TableProps } from 'antd';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { PageTitle } from '@client/components/PageTitle';
import { HideColumns } from '@client/components/table/HideColumns';
import { Toolbar } from '@client/components/Toolbar';
import { EAbilityActions, EAbilitySubjects } from '@client/hooks/useAbilities';
import { useAuth } from '@client/hooks/useAuth';
import { useColumnsToggle } from '@client/hooks/useColumnsToggle';
import { useSubscriptions } from '@client/hooks/useSubscriptions';
import { useTable } from '@client/hooks/useTable';
import { PageLayout } from '@client/layouts/page.layout';
import { columnsMetadata } from '@client/pages/subscriptions/columns.metadata';
import { useAssigned } from '@client/store/assigned.store';
import { useLocale } from '@client/store/locale.store';
import { indexable } from '@client/utils/table/table.util';

import styles from './subscriptions.module.less';

import type { TColumns } from '@client/types/antd.type';
import type { IFeature, ISubscription } from '@common/types';
import type { ItemType } from 'antd/es/menu/interface';

export const SubscriptionsPage: React.FC = () => {
  const { ability } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useLocale();

  const { loading, entities, total, refetch, onDeactivate, onEdit } =
    useSubscriptions();

  const { setAssigned: setAssignedFeatures } = useAssigned<IFeature>();

  useEffect(() => {
    setAssignedFeatures([]);
  }, [setAssignedFeatures]);

  const {
    tableParams: { pagination },
    filteredInfo,
    sortedInfo,
    handleTableChange,
  } = useTable<ISubscription>(entities, total);

  const columns: TableProps<ISubscription>['columns'] = columnsMetadata({
    locale,
    filteredInfo,
    sortedInfo,
    onDelete: onDeactivate,
    onEdit,
    entities,
  });

  const { filteredColumns, columnsList, selectedColumns, setSelectedColumns } =
    useColumnsToggle<ISubscription>(columns as TColumns<ISubscription>[], [
      'createdAt',
    ]);

  const tableProps = {
    columns: filteredColumns as TColumns<ISubscription>[],
    pagination: {
      ...pagination,
    },
    scroll: { x: 1400 },
    bordered: true,
    size: 'small' as const,
    className: styles.gridList,
    dataSource: indexable<ISubscription>(entities),
    loading,
    rowKey: (record: ISubscription) => record.id ?? `row-${Math.random()}`,
    onChange: handleTableChange,
    title: () => (
      <div className={styles.gridHeader}>
        <Toolbar<ISubscription[]>
          onRefresh={refetch}
          entities={entities}
          subject={EAbilitySubjects.SUBSCRIPTIONS}
          items={
            [
              {
                disabled: ability.cannot(
                  EAbilityActions.CREATE,
                  EAbilitySubjects.SUBSCRIPTION,
                ),
                label: t('actions.add', { type: t('subscription') }),
                icon: <PlusOutlined />,
                onClick: () => navigate('/subscriptions/new'),
              },
            ] as unknown as ItemType[]
          }
        >
          <HideColumns
            columnsList={columnsList}
            selectedColumns={selectedColumns}
            onChange={setSelectedColumns}
          />
        </Toolbar>
      </div>
    ),
    footer: () => (
      <div className={styles.gridFooter}>
        {t('table.total', { amount: total })}
      </div>
    ),
  };

  return (
    <PageLayout
      subject={EAbilitySubjects.SUBSCRIPTIONS}
      title={
        <PageTitle Icon={ShoppingCartOutlined} name={t('subscriptions')} />
      }
    >
      <Table<ISubscription> {...tableProps} loading={loading} />
    </PageLayout>
  );
};
